/* eslint-disable react/style-prop-object */
import React, { useMemo, useState } from 'react';
import { Skeleton, Tooltip, Row, Col, Space, Button, Card } from 'antd';
import type { MapRef } from 'react-map-gl';
import MapBox, { Marker, Source, NavigationControl, ScaleControl, Layer } from 'react-map-gl';
import { markerForIndex, fillColorMap } from '../../components/reporting/markerUtils';
import { hex } from 'color-convert';
import { USDefaultBounds } from '../../components/fulfillmentConfig/SimpleMap';
import type {
  ChoroplethStateRecord,
  ChoroplethCountyRecord,
  ChoroplethReportData,
  Simulation,
} from '../../store/simulation/types';
import { SimulationChoroplethSidebar } from './SimulationChoroplethSidebar';
import { useCarriersFiltered } from '../../hooks/queries/useCarriersFiltered';
import { carrierColorMap } from './utils';
import { createUseStyles } from 'react-jss';
import { usePDF } from 'react-to-pdf';
import { DownloadOutlined } from '@ant-design/icons';

export interface ChoroplethMarker {
  latitude: number;
  longitude: number;
  name: string;
  tooltip: React.ReactNode | 'string';
}

export interface Range {
  min?: number;
  max?: number;
  value?: string;
  label: string;
  color: string;
  key: string;
}

export interface SelectedState {
  selectedState?: string;
  selectedCounty?: number;
}

export interface SimulationChoroplethProps {
  loading: boolean | undefined;
  reportData: ChoroplethReportData;
  simulation: Simulation;
}

const useStyles = createUseStyles({
  legendTitle: {
    textAlign: 'center',
    marginBottom: '0px',
  },
  legendWrapper: {
    padding: '10px',
    backgroundColor: '#fff',
    borderRadius: '3px',
    bottom: '10px',
    maxWidth: 'fit-content',
    overflow: 'hidden',
    boxShadow: '0 6px 10px rgba(0, 0, 0, 0.1);',
    position: 'absolute',
    zIndex: 999,
    left: '120px',
  },
  legendLeft: {
    left: '10px',
  },
  legendRight: {
    right: '0px',
  },
  legendRow: {
    padding: '0px 10px 0px 10px',
  },
  key: {
    borderRadius: '50%',
    display: 'inline-block',
    height: '10px',
    marginRight: '10px',
    width: '10px',
  },
});

export const SimulationChoropleth = ({ reportData, loading, simulation }: SimulationChoroplethProps) => {
  const carrierMap = useCarriersFiltered();
  const colorPad = [...fillColorMap];
  const classes = useStyles();
  const mapboxRef = React.useRef<MapRef>(null);
  const [expression, setExpression] = useState(['get', 'STUSPS']);
  const [countyExpression, setCountryExpress] = useState(['get', 'GEOID']);
  const [choroplethHoverState, setChoroplethHoverState] = useState<SelectedState | undefined>();
  const stateReportData = reportData?.stateData;
  // default [] so a partial payload (no countyData) can't crash the sort/forEach below.
  const countyReportData = reportData?.countyData ?? [];
  const originMarkerData = reportData?.originMarkerData;
  const { toPDF, targetRef } = usePDF({ filename: `${simulation.name}-chorolopleth.pdf` });
  // when the window resizes the source layers do not
  // resize if we explictly force this
  mapboxRef?.current?.on('resize', () => {
    mapboxRef.current?.triggerRepaint();
  });
  mapboxRef?.current?.on('load', () => {
    mapboxRef.current?.triggerRepaint();
  });

  const markers = originMarkerData?.map((marker, index) => {
    return {
      latitude: marker.latitude,
      longitude: marker.longitude,
      name: marker.originSlug,
      tooltip: (
        <span>
          {marker.originSlug}
          <br />
          {marker.count.toLocaleString()} total shipments
        </span>
      ),
    };
  });

  const stateMap = useMemo(() => {
    return stateReportData?.reduce<Record<string, ChoroplethStateRecord>>((acc, record) => {
      if (record.stateId) {
        acc[record.stateId.toUpperCase()] = record;
      }
      return acc;
    }, {});
  }, [stateReportData]);

  const countyMap = useMemo(() => {
    return countyReportData?.reduce<Map<string, ChoroplethCountyRecord>>((acc, record) => {
      if (record.countyFips) {
        acc.set(record.countyFips, record);
      }
      if (record.countyFipsAll.length > 1) {
        record.countyFipsAll.forEach((fips) => acc.set(fips, record));
      }
      return acc;
    }, new Map());
  }, [countyReportData]);

  // guard: stateMap/countyMap are undefined mid-refetch (reportData transiently undefined).
  const activeState = choroplethHoverState?.selectedState ? stateMap?.[choroplethHoverState.selectedState] : undefined;
  const activeCounty = choroplethHoverState?.selectedCounty
    ? countyMap?.get(choroplethHoverState.selectedCounty.toString())
    : undefined;

  const mergedRanges: Record<string, Range> = {};

  if (!stateReportData || stateReportData.length === 0) {
    return null;
  }

  countyReportData
    .sort((a, b) => (b?.primaryCarrier || '').localeCompare(a?.primaryCarrier || ''))
    ?.reduce<Record<string, Range>>((prev, cur) => {
      const key = cur.primaryCarrier;

      if (!prev[key]) {
        const carrierColor = carrierColorMap.get(key);
        prev[key] = {
          label: carrierMap[key]?.name || key,
          key: key,
          value: key,
          color: carrierColor || (colorPad.pop() || colorPad[0])?.replace('#', ''),
        };
      }
      return prev;
    }, mergedRanges);

  const unknownColor = 'eaeaea';
  const ranges = Object.values(mergedRanges);

  const mapMarkers =
    markers?.map((marker, index) => {
      const iconUrl = markerForIndex(index);
      return (
        <Marker
          longitude={marker.longitude}
          latitude={marker.latitude}
          key={`zip3choropleth-marker-${index}`}
          anchor="bottom"
        >
          <Tooltip placement="topLeft" title={marker.tooltip}>
            <img src={iconUrl} alt={marker?.name} />
          </Tooltip>
        </Marker>
      );
    }) || [];

  mapMarkers.push(<NavigationControl showCompass={false} key={'zoom'} />);
  mapMarkers.push(<ScaleControl position="bottom-right" unit="imperial" key={'scale'} />);

  return stateReportData && !loading ? (
    <Card bodyStyle={{ paddingTop: 10 }}>
      <Row gutter={32}>
        <Col offset={22} span={2}>
          <Button
            style={{ float: 'right', marginBottom: 5 }}
            onClick={async () => {
              toPDF();
            }}
          >
            <DownloadOutlined />
          </Button>
        </Col>
      </Row>
      <Row>
        <Col flex={'350px'}>
          <SimulationChoroplethSidebar
            activeCounty={activeCounty}
            activeState={activeState}
            ranges={Object.values(mergedRanges)}
          />
        </Col>
        <Col flex="auto">
          <div ref={targetRef}>
            <MapBox
              initialViewState={{
                zoom: 3.5,
                bounds: USDefaultBounds,
              }}
              preserveDrawingBuffer={true}
              ref={mapboxRef}
              cursor={'crosshair'}
              maxZoom={6}
              minZoom={3}
              attributionControl={false}
              mapboxAccessToken={
                'pk.eyJ1IjoibWFjLXNoaXBpdW0iLCJhIjoiY2tkb29jbTBuMGE0eTJybzJzdWFucW9weSJ9.Zk9IJNw95wjvjqGatn8OFw'
              }
              mapStyle={'mapbox://styles/mac-shipium/ckdonm11l29ig1it8ebiyw1ty'}
              style={{
                height: '65vh',
              }}
              onStyleData={(map) => {
                let hoveredId: any = null;
                let countyId: any = null;
                const tempCountyExpression = new Map();
                const tempExpression: any[] = [];

                Object.values(stateReportData).forEach((row) => {
                  const match = ranges.find((record) => record.value === row.primaryCarrier);
                  const rgba = match ? hex.rgb(match?.color) : hex.rgb(unknownColor);
                  const color = `rgba(${rgba?.join(',')}, 1)`;
                  if (row.stateId) {
                    tempExpression.push(row.stateId, color);
                  }
                });

                countyReportData.forEach((row) => {
                  const match = ranges.find((record) => record.value === row.primaryCarrier);
                  const rgba = match ? hex.rgb(match?.color) : hex.rgb(unknownColor);
                  const color = `rgba(${rgba?.join(',')}, 1)`;
                  if (row.countyFipsAll && row.countyFipsAll.length > 1) {
                    row.countyFipsAll.forEach((fips) => {
                      tempCountyExpression.set(fips, color);
                    });
                  } else if (row.countyFips) {
                    tempCountyExpression.set(row.countyFips, color);
                  }
                });

                // Last value is the default, used where there is no data
                tempExpression.push('rgba(230, 230, 230, 1)');
                const countyArray = Array.from(tempCountyExpression.entries()).flat();
                countyArray.push('rgba(230, 230, 230, 1)');

                setExpression([...tempExpression]);
                setCountryExpress([...countyArray]);

                mapboxRef?.current?.on('mousemove', 'zip-join', (e: any) => {
                  if (e.features.length > 0) {
                    if (hoveredId !== null) {
                      mapboxRef?.current?.setFeatureState(
                        { source: 'us-states', sourceLayer: 'cb_2018_us_state_500k-8s7qk5', id: hoveredId },
                        { hover: false },
                      );
                    }
                    hoveredId = e.features[0].id;
                    mapboxRef?.current?.setFeatureState(
                      { source: 'us-states', sourceLayer: 'cb_2018_us_state_500k-8s7qk5', id: hoveredId },
                      { hover: true },
                    );
                  }
                });

                mapboxRef?.current?.on('mouseleave', 'zip-join', () => {
                  if (hoveredId !== null) {
                    mapboxRef?.current?.setFeatureState(
                      { source: 'us-states', sourceLayer: 'cb_2018_us_state_500k-8s7qk5', id: hoveredId },
                      { hover: false },
                    );
                  }
                  hoveredId = null;
                });

                mapboxRef?.current?.on('mousemove', 'zip-join-counties', (e: any) => {
                  if (e.features.length > 0) {
                    hoveredId = null;
                    if (countyId !== null) {
                      mapboxRef?.current?.setFeatureState(
                        { source: 'us-counties', sourceLayer: 'cb_2018_us_county_500k-9ci375', id: countyId },
                        { hover: false },
                      );
                    }
                    countyId = e.features[0].id;
                    mapboxRef?.current?.setFeatureState(
                      { source: 'us-counties', sourceLayer: 'cb_2018_us_county_500k-9ci375', id: countyId },
                      { hover: true },
                    );
                  }
                });

                mapboxRef?.current?.on('mouseleave', 'zip-join-counties', () => {
                  hoveredId = null;
                  if (countyId !== null) {
                    mapboxRef?.current?.setFeatureState(
                      { source: 'us-counties', sourceLayer: 'cb_2018_us_county_500k-9ci375', id: countyId },
                      { hover: false },
                    );
                  }
                  countyId = null;
                });

                mapboxRef?.current?.on('mousemove', (e: any) => {
                  const features = mapboxRef?.current?.queryRenderedFeatures(e.point, {
                    layers: ['zip-join-counties', 'zip-join'],
                  });

                  if (features && Array.isArray(features) && features[0]?.properties?.STUSPS) {
                    const state = features[0].properties?.STUSPS;
                    setChoroplethHoverState({ selectedCounty: 0, selectedState: state });
                  } else if (features && Array.isArray(features) && features[0]?.properties?.GEOID) {
                    const geoId = features[0].properties?.GEOID;
                    setChoroplethHoverState({ selectedCounty: geoId, selectedState: undefined });
                  }
                });

                mapboxRef?.current?.on('click', (e: any) => {
                  const features = mapboxRef?.current?.queryRenderedFeatures(e.point, {
                    layers: ['zip-join-counties', 'zip-join'],
                  });
                  if (features && Array.isArray(features) && features[0]?.properties?.STUSPS) {
                    // const state = features[0].properties?.STUSPS;
                    // TODO show detail modal / drawer on click
                  } else if (features && Array.isArray(features) && features[0]?.properties?.GEOID) {
                    // const geoId = features[0].properties?.GEOID;
                    // const record = countyMap.get(geoId);
                    // TODO show detail modal / drawer on click
                  }
                });
              }}
            >
              {mapMarkers}
              {countyExpression.length >= 2 && (
                <Source id="us-counties" type="vector" url={'mapbox://mac-shipium.87s7ixua'} maxzoom={8} minzoom={4}>
                  <Layer
                    {...{
                      id: 'zip-join-counties',
                      type: 'fill',
                      source: 'us-counties',
                      'source-layer': 'cb_2018_us_county_500k-9ci375',
                      paint: {
                        'fill-color': ['match', ['get', 'GEOID'], ...countyExpression],
                        'fill-outline-color': 'rgba(255, 255, 255, 1)',
                        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0.6],
                      },
                    }}
                  />
                </Source>
              )}
              {expression.length >= 2 && (
                <Source id="us-states" type="vector" url="mapbox://mac-shipium.6sdi99da" maxzoom={7} minzoom={0}>
                  <Layer
                    {...{
                      id: 'zip-join',
                      type: 'fill',
                      source: 'us-states',
                      maxzoom: 4,
                      'source-layer': 'cb_2018_us_state_500k-8s7qk5',
                      paint: {
                        'fill-color': ['match', ['get', 'STUSPS'], ...expression],
                        'fill-outline-color': 'rgba(255, 255, 255, 1)',
                        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0.6],
                      },
                    }}
                  />
                </Source>
              )}
            </MapBox>
            <div id="state-legend" className={classes.legendWrapper}>
              <Space size={[8, 16]} wrap>
                {ranges?.map((row) => {
                  return (
                    <div key={row.key}>
                      <span className={classes.key} style={{ backgroundColor: `#${row.color}`, opacity: 0.75 }}></span>
                      {row.label}
                    </div>
                  );
                })}
              </Space>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  ) : (
    <Skeleton active={true} paragraph={{ rows: 10 }} />
  );
};
